import { createFileRoute, Link } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { ManagedImage } from "@/components/site/ManagedImage";
import { ManagedProse, ManagedSection } from "@/components/site/ManagedProse";
import { PageHero } from "@/components/site/PageHero";
import { businessNode, canonical } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { loadPageSettings } from "@/lib/page-seo-loader";
import { applySeoOverrides } from "@/lib/seo-overrides";
import {
  Users,
  ShieldCheck,
  BadgeCheck,
  Award,
  Star,
  Phone,
  CheckCircle2,
  Target,
  Compass,
  Heart,
} from "lucide-react";

const TITLE = "About Durafoam Roofing | Phoenix Family Roofer Since 1989";
const DESCRIPTION =
  "Founded in 1989 by Steve Forstie, now run by sons Tim & Curtis. Family-owned Phoenix foam, tile & shingle roofing. Licensed ROC223900, BBB accredited.";


function localBusinessSchema(rating: number, reviewCount: number) {
  return businessNode({
    url: "/about-us",
    aggregateRating: {
      ratingValue: rating.toFixed(1),
      reviewCount: String(reviewCount),
    },
  });
}


const knowsAbout = [
  "Foam roofing",
  "Spray polyurethane foam roofing",
  "Roof recoating",
  "Tile roofing",
  "Phoenix roofing",
  "Commercial roofing",
];

const personSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Steve Forstie",
    jobTitle: "Founder",
    worksFor: {
      "@type": "RoofingContractor",
      name: "Durafoam Roofing",
      url: "https://www.durafoaminc.com",
    },
    description:
      "Steve Forstie founded Durafoam Roofing in 1989 with one principle: produce the highest quality roof possible at an affordable cost.",
    knowsAbout,
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Tim Forstie",
    jobTitle: "Owner",
    worksFor: {
      "@type": "RoofingContractor",
      name: "Durafoam Roofing",
      url: "https://www.durafoaminc.com",
    },
    description:
      "Tim Forstie is co-owner of Durafoam Roofing, a family-owned Phoenix roofing company founded by his father Steve Forstie in 1989.",
    knowsAbout,
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Curtis Forstie",
    jobTitle: "Owner",
    worksFor: {
      "@type": "RoofingContractor",
      name: "Durafoam Roofing",
      url: "https://www.durafoaminc.com",
    },
    description:
      "Curtis Forstie is co-owner of Durafoam Roofing, a family-owned Phoenix roofing company founded by his father Steve Forstie in 1989.",
    knowsAbout,
  },
];

type FaqItem = { q: string; a: React.ReactNode; plain: string };

const faqs: FaqItem[] = [
  {
    q: "How long has Durafoam Roofing been in business?",
    a: (
      <>
        Durafoam Roofing was founded in 1989 by Steve Forstie and has served Arizona homeowners,
        builders, and property owners since 1989.
      </>
    ),
    plain:
      "Durafoam Roofing was founded in 1989 by Steve Forstie and has served Arizona homeowners, builders, and property owners since 1989.",
  },
  {
    q: "Is Durafoam Roofing family-owned?",
    a: (
      <>
        Yes. Durafoam Roofing is a family-owned roofing company. Steve Forstie founded the
        company, and his sons Tim and Curtis Forstie continue to carry forward the same
        commitment to quality workmanship, honest recommendations, and long-term customer
        relationships.
      </>
    ),
    plain:
      "Yes. Durafoam Roofing is a family-owned roofing company. Steve Forstie founded the company, and his sons Tim and Curtis Forstie continue to carry forward the same commitment to quality workmanship, honest recommendations, and long-term customer relationships.",
  },
  {
    q: "What areas does Durafoam Roofing serve?",
    a: (
      <>
        Durafoam Roofing serves Phoenix and surrounding Valley communities, including{" "}
        <Link to="/scottsdale-roofing-contractor" className="text-accent underline">Scottsdale</Link>,{" "}
        <Link to="/paradise-valley-roofing" className="text-accent underline">Paradise Valley</Link>,{" "}
        <Link to="/mesa-roofing-contractor" className="text-accent underline">Mesa</Link>,{" "}
        <Link to="/cave-creek-roofing-contractor" className="text-accent underline">Cave Creek</Link>,{" "}
        <Link to="/carefree-roofing-contractor" className="text-accent underline">Carefree</Link>,{" "}
        <Link to="/fountain-hills-roofing-contractor" className="text-accent underline">Fountain Hills</Link>,{" "}
        <Link to="/sun-city-roofing-contractor" className="text-accent underline">Sun City</Link>,{" "}
        <Link to="/peoria-roofing-contractor" className="text-accent underline">Peoria</Link>, and nearby areas.
      </>
    ),
    plain:
      "Durafoam Roofing serves Phoenix and surrounding Valley communities, including Scottsdale, Paradise Valley, Mesa, Cave Creek, Carefree, Fountain Hills, Sun City, Peoria, and nearby areas.",
  },
  {
    q: "What types of roofing does Durafoam Roofing work on?",
    a: (
      <>
        Durafoam Roofing works on{" "}
        <Link to="/foam-roofing" className="text-accent underline">foam roofs</Link>,{" "}
        <Link to="/tile-roofing" className="text-accent underline">tile roofs</Link>,{" "}
        <Link to="/shingle-roofing" className="text-accent underline">shingle roofs</Link>, flat roofs,{" "}
        <Link to="/foam-roof-coating-recoating" className="text-accent underline">roof coatings</Link>,{" "}
        <Link to="/roof-repair" className="text-accent underline">roof repairs</Link>,{" "}
        <Link to="/roof-maintenance" className="text-accent underline">roof maintenance</Link>,{" "}
        <Link to="/roof-leak-repair" className="text-accent underline">roof leak repair</Link>,{" "}
        <Link to="/skylight-repair" className="text-accent underline">skylights</Link>, and{" "}
        <Link to="/services" className="text-accent underline">roof replacement projects</Link>.
      </>
    ),
    plain:
      "Durafoam Roofing works on foam roofs, tile roofs, shingle roofs, flat roofs, roof coatings, roof repairs, roof maintenance, roof leak repair, skylights, and roof replacement projects.",
  },
  {
    q: "Why do customers choose Durafoam Roofing?",
    a: (
      <>
        Many customers choose Durafoam Roofing because of its long local history, family-owned
        accountability, repair-first approach, referral reputation, and experience with Arizona
        roof systems. Many Durafoam customers have worked with the company for decades.
      </>
    ),
    plain:
      "Many customers choose Durafoam Roofing because of its long local history, family-owned accountability, repair-first approach, referral reputation, and experience with Arizona roof systems. Many Durafoam customers have worked with the company for decades.",
  },
  {
    q: "Does Durafoam Roofing offer free roof inspections?",
    a: (
      <>
        Yes. Durafoam Roofing offers{" "}
        <Link to="/free-roof-inspection-phoenix" className="text-accent underline">
          free roof inspections
        </Link>{" "}
        for homeowners and property owners throughout the Phoenix Valley. The team can inspect
        the roof, explain what they find, and recommend the next step.
      </>
    ),
    plain:
      "Yes. Durafoam Roofing offers free roof inspections for homeowners and property owners throughout the Phoenix Valley. The team can inspect the roof, explain what they find, and recommend the next step.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.plain },
  })),
};

export const Route = createFileRoute("/about-us")({
  loader: () => loadPageSettings("about-us"),
  head: ({ loaderData }) => {
    const rating = loaderData?.rating ?? 4.8;
    const reviewCount = loaderData?.reviewCount ?? 84;
    const url = canonical("/about-us");
    return applySeoOverrides({
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        ...ogImageMeta("about-us"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(localBusinessSchema(rating, reviewCount)),
        },
        ...personSchemas.map((s) => ({
          type: "application/ld+json",
          children: JSON.stringify(s),
        })),
        {
          type: "application/ld+json",
          children: JSON.stringify(faqSchema),
        },
      ],
    }, loaderData?.seo);
  },
  component: AboutPage,
});

const trust = [
  {
    icon: Users,
    title: "Family-Owned Since 1989",
    body: "Founded by Steve Forstie and now run by his sons Tim and Curtis — same family, same name, same standards.",
  },
  {
    icon: ShieldCheck,
    title: "Arizona Licensed ROC223900",
    body: "Fully licensed, bonded, and insured. License L-42 in the state of Arizona.",
  },
  {
    icon: BadgeCheck,
    title: "BBB Accredited Since 2008",
    body: "A+ rated with the Better Business Bureau. Long-term track record you can verify.",
  },
  {
    icon: Award,
    title: "Rosie on the House Certified",
    body: "Rosie on the House Certified Partner since 1996 — nearly 30 years of on-air recommendations.",
  },
  {
    icon: Star,
    title: "A Long List of 4.8-Star Google Reviews",
    body: "84 verified Google reviews from Phoenix homeowners going back over a decade.",
  },
  {
    icon: CheckCircle2,
    title: "Repair-First, Honest Assessments",
    body: "If a roof can be repaired properly, we say so. If it needs replacement, we explain why.",
  },
];

const mvv = [
  {
    icon: Target,
    title: "Our Mission",
    body: "Provide Arizona homeowners and businesses with high-quality roofing at a fair price, using honest recommendations, skilled workmanship, and a repair-first mindset whenever possible.",
  },
  {
    icon: Compass,
    title: "Our Vision",
    body: "Carry Durafoam Roofing forward as one of Arizona's most trusted family-owned roofing companies — built on long-term relationships, referral work, and roofs that stand up to the desert for years to come.",
  },
  {
    icon: Heart,
    title: "Our Values",
    body: "Quality workmanship. Honest communication. Repair-first recommendations. Family-owned accountability. Roofing built for Arizona.",
  },
];

function AboutPage() {
  return (
    <>
      {/* Hero */}
      <PageHero
        pageSlug="about-us"
        eyebrow="About Durafoam Roofing"
        h1="Family-owned Phoenix roofing since 1989."
        subhead="Built by Steve Forstie in 1989 and run today by his sons Tim and Curtis. Foam, tile, shingle, and flat roofing across the Phoenix Valley since 1989 — honest assessments, careful work, and roofs built to last in the desert."
      />


      {/* Built on quality from day one */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
            <div className="space-y-5 text-[16px] leading-relaxed text-foreground/85">
              <h2 className="text-3xl font-bold text-primary">Built on quality from day one</h2>
              <ManagedProse pageSlug="about-us" sectionKey="intro" className="space-y-5">
                <p>
                  Durafoam Roofing was founded in 1989 by <strong>Steve Forstie</strong> with one
                  simple principle: produce the highest quality roof possible at an affordable cost.
                  That mindset shaped everything Durafoam Roofing became — from residential foam
                  roofs and tile roofs to custom homes and large commercial roofing projects.
                </p>
                <p>
                  Steve built the company by doing the work the right way, standing behind the
                  finished product, and treating customers with respect. Over the years, Durafoam
                  earned the trust of prominent custom home builders, high-profile residences, and
                  major commercial roofing companies throughout the Valley.
                </p>
              </ManagedProse>
            </div>
            <figure>
              <ManagedImage pageSlug="about-us" slot="founder" variant="portrait" />
              <figcaption className="mt-3 text-sm text-[var(--muted-foreground)]">
                Steve Forstie — founder, Durafoam Roofing (1989).
              </figcaption>
            </figure>
          </div>
        </Container>
      </section>

      {/* A family business passed from father to sons */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl space-y-5 text-[16px] leading-relaxed text-foreground/85">
            <h2 className="text-3xl font-bold text-primary">
              A family business passed from father to sons
            </h2>
            <ManagedProse pageSlug="about-us" sectionKey="process" className="space-y-5">
              <p>
                In 2011, Steve began transitioning the company to his sons,{" "}
                <strong>Tim and Curtis Forstie</strong>. But this was not a handoff from the
                sidelines. Tim and Curtis grew up in the business — they learned roofing from the
                ground up: job sites, materials, crews, customer service, project management, and
                the details that separate a quick patch from a roof built to last.
              </p>
              <p>
                Durafoam is not run by people who only understand roofing from a spreadsheet. It is
                run by a family that understands the work, the crews, the customers, and the
                responsibility that comes with stepping onto someone's roof.
              </p>
            </ManagedProse>
          </div>
          <div className="mx-auto mt-10 max-w-3xl space-y-10">
            <ManagedSection pageSlug="about-us" sectionKey="story" heading="Our story, in their words" />
            <ManagedSection pageSlug="about-us" sectionKey="main" heading="In our experience" />
          </div>
        </Container>
      </section>

      {/* Mission, Vision, Values */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-primary">Mission, vision &amp; values</h2>
            <p className="mt-3 text-[var(--muted-foreground)]">
              Durafoam Roofing was built on more than roofing materials. It was built on a
              standard — that a roof should be done right, priced fairly, and backed by people
              who care about the finished product.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {mvv.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-xl border border-border bg-white p-6">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-[var(--surface)] text-accent">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-primary">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">{body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Reputation through hard times */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl space-y-5 text-[16px] leading-relaxed text-foreground/85">
            <h2 className="text-3xl font-bold text-primary">
              A reputation that carried us through hard times
            </h2>
            <ManagedProse pageSlug="about-us" sectionKey="why_us" className="space-y-5">
              <p>
                Many roofing companies did not survive the Great Recession. Durafoam did. Not
                because of flashy marketing. Not because of shortcuts. Durafoam continued forward
                because of its history, its referral work, and thousands of satisfied customers who
                trusted the company enough to call again and recommend it to others.
              </p>
              <p>
                That kind of reputation is not built overnight. Since 1989, it has been built by
                showing up, doing the job right, and taking care of people even after the work is
                done. Many Durafoam customers have been with the company for more than 30 years —
                that says more than any advertisement ever could.
              </p>
            </ManagedProse>
          </div>
        </Container>
      </section>

      {/* Roofing built for Arizona */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl space-y-5 text-[16px] leading-relaxed text-foreground/85">
            <h2 className="text-3xl font-bold text-primary">
              Roofing built for Arizona homes and businesses
            </h2>
            <ManagedProse pageSlug="about-us" sectionKey="climate" className="space-y-5">
              <p>
                Today, Tim and Curtis continue the work ethic and commitment to excellence started
                by their father. Durafoam Roofing provides{" "}
                <Link to="/roof-repair" className="text-accent underline">roof repair</Link>,{" "}
                <Link to="/foam-roofing" className="text-accent underline">foam roofing</Link>,
                tile roof repair,{" "}
                <Link to="/services" className="text-accent underline">roof replacement</Link>,{" "}
                <Link to="/roof-maintenance" className="text-accent underline">roof maintenance</Link>,
                and{" "}
                <Link to="/free-roof-inspection-phoenix" className="text-accent underline">
                  free roof inspections
                </Link>{" "}
                across the Phoenix Valley.
              </p>
              <p>
                Arizona roofs take a beating from extreme heat, UV exposure, monsoon storms, wind,
                and age. Our job is to inspect the roof honestly, explain the options clearly, and
                recommend the repair,{" "}
                <Link to="/roof-maintenance" className="text-accent underline">maintenance</Link>,{" "}
                <Link to="/foam-roof-coating-recoating" className="text-accent underline">recoating</Link>,
                or replacement that actually makes sense.
              </p>
              <p>
                We believe in a repair-first approach whenever possible. If a roof can be repaired
                properly, we will say so. If it needs to be replaced, we will explain why. Either
                way, the goal is the same: protect the property with roofing work that lasts.
              </p>
            </ManagedProse>
          </div>
        </Container>
      </section>

      {/* Trust grid */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-primary">Why Phoenix homeowners trust us</h2>
            <p className="mt-3 text-[var(--muted-foreground)]">
              Credentials, ratings, and the boring stuff that actually matters when someone's on
              your roof.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {trust.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-xl border border-border bg-white p-6">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-[var(--surface)] text-accent">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-primary">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">{body}</p>
                {title === "Rosie on the House Certified" ? (
                  <Link
                    to="/rosie-on-the-house"
                    className="mt-3 inline-block text-sm font-semibold text-accent hover:underline"
                  >
                    See every appearance →
                  </Link>
                ) : null}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-primary">
              Frequently asked questions about Durafoam Roofing
            </h2>
            <div className="mt-8 divide-y divide-border rounded-xl border border-border bg-white">
              {faqs.map((f) => (
                <details key={f.q} className="group p-5">
                  <summary className="cursor-pointer list-none text-lg font-semibold text-primary marker:hidden">
                    {f.q}
                  </summary>
                  <div className="mt-3 text-[15px] leading-relaxed text-foreground/80">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Need help with your roof?
          </h2>
          <ManagedProse
            pageSlug="about-us"
            sectionKey="cta"
            className="mx-auto mt-3 max-w-2xl space-y-3 text-white/80"
          >
            <p>
              Whether it's a free inspection, a repair estimate, or an active roof leak —
              Durafoam Roofing is ready to help.
            </p>
          </ManagedProse>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/contact-us" variant="primary" size="lg">
              Contact us
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
